import { fetchJson, fetchTextOk } from './http'

const SDK_VERSION = 'aime.ui-plugin.sdk.v1'

function stageError(stage, error) {
  const message = error?.message || String(error)
  throw new Error(`${stage}: ${message}`)
}

export function makePluginsApi() {
  const listInstallations = () => fetchJson('/api/plugins/installations')
  const listPlugins = () => fetchJson('/api/plugins/plugins')
  const listEngines = () => fetchJson('/api/plugins/engines')
  const updatePlugin = (pluginId, payload) => fetchJson(`/api/plugins/plugins/${encodeURIComponent(pluginId)}`, {
    method: 'PATCH', body: JSON.stringify(payload),
  })
  const listCharacters = () => fetchJson('/api/plugins/characters')
  const listCharacterCatalog = () => fetchJson('/api/plugins/characters/catalog')
  const acquireCharacter = (payload) => fetchJson('/api/plugins/characters/acquire', {
    method: 'POST',
    body: JSON.stringify({ ...payload, confirm: true }),
  })
  const prepareCharacterPurchase = (payload) => fetchJson('/api/plugins/characters/purchase/prepare', {
    method: 'POST',
    body: JSON.stringify({ ...payload, confirm: true }),
  })
  const confirmCharacterPurchase = (purchaseId, signature) => fetchJson('/api/plugins/characters/purchase/confirm', {
    method: 'POST',
    body: JSON.stringify({ purchase_id: purchaseId, signature, confirm: true }),
  })
  const bindCharacter = (installationId, walletPublicKey, characterId) => fetchJson(
    `/api/plugins/installations/${encodeURIComponent(installationId)}/character`,
    {
      method: 'PUT',
      body: JSON.stringify({ wallet_public_key: walletPublicKey, character_id: characterId, confirm: true }),
    },
  )
  const unbindCharacter = (installationId, walletPublicKey) => fetchJson(
    `/api/plugins/installations/${encodeURIComponent(installationId)}/character`,
    {
      method: 'DELETE',
      body: JSON.stringify({ wallet_public_key: walletPublicKey, confirm: true }),
    },
  )
  const resetCharacterAbility = payload => fetchJson('/api/plugins/characters/ability/reset', {
    method: 'POST',
    body: JSON.stringify({ ...payload, confirm: true }),
  })

  const createPlugin = async ({ slug, name, description = '', kind = 'game', category = 'games', engine, tags = [] }) => {
    try {
      return await fetchJson('/api/plugins/plugins', {
        method: 'POST',
        body: JSON.stringify({ slug, name, description, kind, category, engine, tags, sdk_version: SDK_VERSION }),
      })
    } catch (error) {
      stageError('Create extension', error)
    }
  }

  const listVersions = pluginId => fetchJson(`/api/plugins/plugins/${encodeURIComponent(pluginId)}/versions`)

  const publishVersion = async (pluginId, html, { install = true, semanticVersion = '0.1.0', capabilities = ['live', 'trade_intent'], assets = [] } = {}) => {
    const payloadAssets = assets.length > 0 ? assets : [{ path: 'index.html', content_type: 'text/html', text: html }]
    try {
      return await fetchJson(`/api/plugins/plugins/${encodeURIComponent(pluginId)}/versions`, {
        method: 'POST',
        body: JSON.stringify({
          semantic_version: semanticVersion,
          entrypoint: 'index.html',
          sdk_version: SDK_VERSION,
          runtime: 'iframe',
          manifest: { sdk_version: SDK_VERSION, capabilities, network: { allowed_origins: [] } },
          assets: payloadAssets,
          publish: true,
          install,
        }),
      })
    } catch (error) {
      stageError('Publish version', error)
    }
  }

  const installPlugin = async (pluginId, versionId = '') => {
    try {
      return await fetchJson('/api/plugins/install', {
        method: 'POST',
        body: JSON.stringify({ plugin_id: pluginId, version_id: versionId }),
      })
    } catch (error) {
      stageError('Install version', error)
    }
  }

  const updateInstallation = (installationId, versionId) => fetchJson(`/api/plugins/installations/${encodeURIComponent(installationId)}/update`, {
    method: 'POST', body: JSON.stringify({ version_id: versionId }),
  })

  const activateInstallation = async installationId => {
    try {
      return await fetchJson(`/api/plugins/installations/${encodeURIComponent(installationId)}/activate`, { method: 'POST' })
    } catch (error) {
      stageError('Activate extension', error)
    }
  }

  const deactivateInstallation = async installationId => {
    try {
      return await fetchJson(`/api/plugins/installations/${encodeURIComponent(installationId)}/deactivate`, { method: 'POST' })
    } catch (error) {
      stageError('Deactivate extension', error)
    }
  }

  const uninstallInstallation = async installationId => {
    try {
      return await fetchJson(`/api/plugins/installations/${encodeURIComponent(installationId)}/uninstall`, { method: 'POST' })
    } catch (error) {
      stageError('Remove extension', error)
    }
  }

  const deleteDraft = (pluginId, versionId) => fetchJson(
    `/api/plugins/authoring/plugins/${encodeURIComponent(pluginId)}/versions/${encodeURIComponent(versionId)}/draft`,
    { method: 'DELETE' },
  )

  const deleteDraftOnlyPlugin = pluginId => fetchJson(
    `/api/plugins/authoring/plugins/${encodeURIComponent(pluginId)}`,
    { method: 'DELETE' },
  )

  const reopenPublishedAsDraft = (pluginId, sourceVersionId, semanticVersion = '') => fetchJson(
    `/api/plugins/authoring/plugins/${encodeURIComponent(pluginId)}/versions/${encodeURIComponent(sourceVersionId)}/reopen`,
    { method: 'POST', body: JSON.stringify({ source_version_id: sourceVersionId, semantic_version: semanticVersion }) },
  )

  const loadInstalledEntrypoint = installationId => fetchTextOk(`/api/plugins/runtime/${encodeURIComponent(installationId)}/index.html`)

  return {
    listInstallations,
    listPlugins,
    listEngines,
    updatePlugin,
    listCharacters,
    listCharacterCatalog,
    bindCharacter,
    unbindCharacter,
    resetCharacterAbility,
    acquireCharacter,
    prepareCharacterPurchase,
    confirmCharacterPurchase,
    createPlugin,
    listVersions,
    publishVersion,
    installPlugin,
    updateInstallation,
    activateInstallation,
    deactivateInstallation,
    uninstallInstallation,
    deleteDraft,
    deleteDraftOnlyPlugin,
    reopenPublishedAsDraft,
    loadInstalledEntrypoint,
  }
}
